import { FC, useEffect, useId, useRef, useState } from 'react'
import cx from 'classnames'
import { AriaButtonProps, useButton } from 'react-aria'
import { colors } from 'src/theme'

import { Icons } from '../Icon'
import { FormikFormProps } from '../../form/FormikForm'
import FormikField from '../../form/FormikField'
import { useOnClickOutside } from '../../hooks'

import * as SC from './styled'

export type HeaderSearchProps = {
  className?: string
  button: AriaButtonProps & {
    label: string
  }
  formikFormProps?: FormikFormProps<SearchFormProps>
  form: {
    placeholder?: string
    submitLabel?: string
    resetLabel?: string
  }
}

const HeaderSearch: FC<HeaderSearchProps> = ({
  className,
  button,
  formikFormProps,
  form,
}) => {
  const id = useId()
  const ref = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)

  const handleToggle = () => {
    setOpen(!open)
  }

  const handleManualClose = () => {
    setOpen(false)
    refTrigger.current?.focus()
  }

  const refTrigger = useRef<HTMLButtonElement>(null)
  const { buttonProps } = useButton(
    {
      'aria-controls': id,
      onPress: handleToggle,
      'aria-expanded': open,
      'aria-label': button['aria-label'],
    },
    refTrigger
  )

  const refInput = useRef<HTMLInputElement>(null)

  // close on click outside
  useOnClickOutside(ref, () => setOpen(false))

  // close the switch on "escape"
  useEffect(() => {
    if (open === false) return
    const handlePressEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleManualClose()
      }
    }
    window.addEventListener('keydown', handlePressEscape)
    return () => {
      window.removeEventListener('keydown', handlePressEscape)
    }
  }, [open])

  // focus the input on open
  useEffect(() => {
    if (!refInput.current || open === false) return
    refInput.current.focus()
  }, [open])

  return (
    <SC.Container className={cx('HeaderSearch', className)} ref={ref}>
      <SC.Trigger {...buttonProps} ref={refTrigger} aria-label={button.label}>
        <SC._Icon icon={Icons.search} aria-hidden />
      </SC.Trigger>
      {formikFormProps && (
        <SC._FormikForm
          formProps={{ hidden: !open, id: id }}
          {...formikFormProps}
        >
          {({ handleReset }) => (
            <>
              <FormikField
                id={`${id}-input`}
                name="term"
                ref={refInput}
                type="search"
                placeholder={form?.placeholder}
                Component={SC.Input}
              />
              {form.placeholder && (
                <SC.Placeholder htmlFor={`${id}-input`}>
                  {form.placeholder}
                </SC.Placeholder>
              )}
              <SC.Reset
                type="reset"
                aria-label={form.resetLabel}
                onClick={handleReset}
              >
                <SC.ResetIcon
                  icon={Icons.closeSmall}
                  color={colors.black}
                  aria-hidden
                />
              </SC.Reset>
              <SC.Submit type="submit" aria-label={form.submitLabel}>
                <SC.SubmitIcon
                  icon={Icons.search}
                  color={colors.black}
                  aria-hidden
                />
              </SC.Submit>
            </>
          )}
        </SC._FormikForm>
      )}
    </SC.Container>
  )
}

export default HeaderSearch
